import { GameSettingsContent } from '../data';
import { createLogger, Logger } from '../utils/Logger';

export interface RuntimeTestingModeState {
  readonly enabled: boolean;
  readonly disablePlayerDamage: boolean;
  readonly disablePlayerDeath: boolean;
  readonly earlyBoss: boolean;
  readonly pauseWaves: boolean;
  readonly verboseCombatDiagnostics: boolean;
  readonly suppressCameraFeedback: boolean;
  readonly diagnosticsOverlay: boolean;
  readonly bossSpawnTimeOverride: number | null;
  readonly bossesToWinOverride: number | null;
}

export interface RuntimeTestingModeQueryInput {
  readonly query: URLSearchParams | string;
  readonly logger?: Logger;
}

export const DEFAULT_RUNTIME_TESTING_MODE_STATE: RuntimeTestingModeState = Object.freeze({
  enabled: false,
  disablePlayerDamage: false,
  disablePlayerDeath: false,
  earlyBoss: false,
  pauseWaves: false,
  verboseCombatDiagnostics: false,
  suppressCameraFeedback: false,
  diagnosticsOverlay: false,
  bossSpawnTimeOverride: null,
  bossesToWinOverride: null
});

const EARLY_BOSS_DEFAULT_SECONDS = 10;
const logger = createLogger('RuntimeTestingMode');

export function parseRuntimeTestingModeState(
  input: URLSearchParams | string,
  inputLogger: Logger = logger
): RuntimeTestingModeState {
  const params = typeof input === 'string'
    ? new URLSearchParams(input.startsWith('?') ? input.slice(1) : input)
    : input;

  const parsed: RuntimeTestingModeState = {
    ...DEFAULT_RUNTIME_TESTING_MODE_STATE,
    enabled: readBooleanFlag(params, ['testing', 'test'], false, inputLogger),
    disablePlayerDamage: readBooleanFlag(params, ['noPlayerDamage', 'disablePlayerDamage'], false, inputLogger),
    disablePlayerDeath: readBooleanFlag(params, ['noPlayerDeath', 'disablePlayerDeath'], false, inputLogger),
    earlyBoss: readBooleanFlag(params, ['earlyBoss'], false, inputLogger),
    pauseWaves: readBooleanFlag(params, ['pauseWaves'], false, inputLogger),
    verboseCombatDiagnostics: readBooleanFlag(params, ['combatDebug', 'verboseCombatDiagnostics'], false, inputLogger),
    suppressCameraFeedback: readBooleanFlag(params, ['suppressCameraFeedback', 'noCameraFx'], false, inputLogger),
    diagnosticsOverlay: readBooleanFlag(params, ['diagnostics', 'diag'], false, inputLogger),
    bossSpawnTimeOverride: readPositiveNumber(params, 'bossSpawnTime', null, inputLogger),
    bossesToWinOverride: readPositiveInteger(params, 'bossesToWin', null, inputLogger)
  };

  const hasAnyToggle = parsed.disablePlayerDamage
    || parsed.disablePlayerDeath
    || parsed.earlyBoss
    || parsed.pauseWaves
    || parsed.verboseCombatDiagnostics
    || parsed.suppressCameraFeedback
    || parsed.diagnosticsOverlay
    || parsed.bossSpawnTimeOverride !== null
    || parsed.bossesToWinOverride !== null;

  const enabled = parsed.enabled || hasAnyToggle;
  const normalized = normalizeRuntimeTestingModeState({
    ...parsed,
    enabled
  }, inputLogger);

  inputLogger.debug('parseRuntimeTestingModeState:parsed-query', {
    query: params.toString(),
    normalized
  });

  if (normalized.enabled) {
    inputLogger.info('parseRuntimeTestingModeState:testing-enabled', normalized);
  }

  return normalized;
}

export function updateRuntimeTestingModeState(
  previous: RuntimeTestingModeState,
  patch: Partial<RuntimeTestingModeState>,
  inputLogger: Logger = logger
): RuntimeTestingModeState {
  const next = normalizeRuntimeTestingModeState({
    ...previous,
    ...patch
  }, inputLogger);

  inputLogger.info('updateRuntimeTestingModeState:toggle-change', {
    previous,
    next
  });

  return next;
}

export function normalizeRuntimeTestingModeState(
  state: RuntimeTestingModeState,
  inputLogger: Logger = logger
): RuntimeTestingModeState {
  let bossSpawnTimeOverride = state.bossSpawnTimeOverride;
  if (bossSpawnTimeOverride !== null && (!Number.isFinite(bossSpawnTimeOverride) || bossSpawnTimeOverride <= 0)) {
    inputLogger.warn('normalizeRuntimeTestingModeState:invalid-bossSpawnTimeOverride', {
      bossSpawnTimeOverride
    });
    bossSpawnTimeOverride = null;
  }

  let bossesToWinOverride = state.bossesToWinOverride;
  if (bossesToWinOverride !== null && (!Number.isInteger(bossesToWinOverride) || bossesToWinOverride <= 0)) {
    inputLogger.warn('normalizeRuntimeTestingModeState:invalid-bossesToWinOverride', {
      bossesToWinOverride
    });
    bossesToWinOverride = null;
  }

  return Object.freeze({
    enabled: state.enabled,
    disablePlayerDamage: state.disablePlayerDamage,
    disablePlayerDeath: state.disablePlayerDeath,
    earlyBoss: state.earlyBoss,
    pauseWaves: state.pauseWaves,
    verboseCombatDiagnostics: state.verboseCombatDiagnostics,
    suppressCameraFeedback: state.suppressCameraFeedback,
    diagnosticsOverlay: state.diagnosticsOverlay,
    bossSpawnTimeOverride,
    bossesToWinOverride
  });
}

export function isRuntimeTestingModeEnabled(state: RuntimeTestingModeState | null | undefined): boolean {
  return Boolean(state?.enabled);
}

export function applyRuntimeTestingOverrides(
  baseSettings: GameSettingsContent,
  testingMode: RuntimeTestingModeState,
  inputLogger: Logger = logger
): GameSettingsContent {
  if (!testingMode.enabled) {
    return baseSettings;
  }

  const nextBossSpawnTime = testingMode.bossSpawnTimeOverride
    ?? (testingMode.earlyBoss ? EARLY_BOSS_DEFAULT_SECONDS : baseSettings.bossSpawnTime);
  const encounterSchedule = createTestingEncounterSchedule(baseSettings, nextBossSpawnTime, inputLogger);
  const availableRequiredEncounterIds = encounterSchedule
    .filter(encounter => encounter.requiredForVictory !== false)
    .map(encounter => encounter.id);
  const requestedBossesToWin = testingMode.bossesToWinOverride ?? baseSettings.bossesToWin;
  const clampedBossesToWin = Math.max(1, Math.min(requestedBossesToWin, availableRequiredEncounterIds.length));
  if (requestedBossesToWin > availableRequiredEncounterIds.length) {
    inputLogger.warn('applyRuntimeTestingOverrides:bossesToWin-clamped', {
      requestedBossesToWin,
      availableRequiredEncounters: availableRequiredEncounterIds.length
    });
  }
  const requiredEncounterIds = availableRequiredEncounterIds.slice(0, clampedBossesToWin);
  const firstRequiredEncounter = encounterSchedule.find(encounter => encounter.id === requiredEncounterIds[0]) ?? encounterSchedule[0];
  const overriddenSettings: GameSettingsContent = Object.freeze({
    ...baseSettings,
    bossSpawnTime: firstRequiredEncounter?.spawnTime ?? nextBossSpawnTime,
    bossesToWin: clampedBossesToWin,
    bossEnemyId: firstRequiredEncounter?.enemyId ?? baseSettings.bossEnemyId,
    bossEncounters: encounterSchedule,
    bossVictoryRequirements: {
      requiredEncounterIds,
      requiredBossCount: clampedBossesToWin
    }
  });

  if (overriddenSettings.bossSpawnTime !== baseSettings.bossSpawnTime || overriddenSettings.bossesToWin !== baseSettings.bossesToWin) {
    inputLogger.info('applyRuntimeTestingOverrides:encounter-schedule-overridden', {
      earlyBoss: testingMode.earlyBoss,
      bossSpawnTimeOverride: testingMode.bossSpawnTimeOverride,
      bossesToWinOverride: testingMode.bossesToWinOverride,
      effectiveBossSpawnTime: overriddenSettings.bossSpawnTime,
      effectiveBossesToWin: overriddenSettings.bossesToWin,
      requiredEncounterIds
    });
  }

  inputLogger.debug('applyRuntimeTestingOverrides:effective-settings', {
    enabled: testingMode.enabled,
    earlyBoss: testingMode.earlyBoss,
    pauseWaves: testingMode.pauseWaves,
    bossSpawnTime: overriddenSettings.bossSpawnTime,
    bossesToWin: overriddenSettings.bossesToWin,
    encounterSchedule: encounterSchedule.map(encounter => ({
      id: encounter.id,
      spawnTime: encounter.spawnTime,
      requiredForVictory: encounter.requiredForVictory !== false
    }))
  });

  return overriddenSettings;
}

function createTestingEncounterSchedule(
  baseSettings: GameSettingsContent,
  firstRequiredEncounterSpawnTime: number,
  inputLogger: Logger
): readonly NonNullable<GameSettingsContent['bossEncounters']>[number][] {
  const baseEncounters = baseSettings.bossEncounters ?? [];
  if (baseEncounters.length === 0) {
    return [];
  }

  const schedule = baseEncounters.map(encounter => ({ ...encounter }));
  const firstRequiredIndex = schedule.findIndex(encounter => encounter.requiredForVictory !== false);
  const targetIndex = firstRequiredIndex >= 0 ? firstRequiredIndex : 0;
  const targetEncounter = schedule[targetIndex];
  if (!targetEncounter) {
    return schedule;
  }

  targetEncounter.spawnTime = firstRequiredEncounterSpawnTime;
  for (let index = 1; index < schedule.length; index += 1) {
    const previousEncounter = schedule[index - 1];
    const currentEncounter = schedule[index];
    if (!previousEncounter || !currentEncounter) {
      continue;
    }

    if (currentEncounter.spawnTime <= previousEncounter.spawnTime) {
      const adjustedSpawnTime = previousEncounter.spawnTime + 0.001;
      inputLogger.warn('applyRuntimeTestingOverrides:encounter-timing-adjusted-for-monotonicity', {
        encounterId: currentEncounter.id,
        previousEncounterId: previousEncounter.id,
        previousSpawnTime: previousEncounter.spawnTime,
        originalSpawnTime: currentEncounter.spawnTime,
        adjustedSpawnTime
      });
      currentEncounter.spawnTime = adjustedSpawnTime;
    }
  }

  return schedule;
}

function readBooleanFlag(
  params: URLSearchParams,
  names: readonly string[],
  fallback: boolean,
  inputLogger: Logger
): boolean {
  for (const name of names) {
    if (!params.has(name)) continue;
    const raw = params.get(name);
    if (raw === null || raw.length === 0) return true;
    const normalized = raw.toLowerCase();
    if (['1', 'true', 'yes', 'on'].includes(normalized)) return true;
    if (['0', 'false', 'no', 'off'].includes(normalized)) return false;
    inputLogger.warn('readBooleanFlag:invalid-value', { name, raw });
    return fallback;
  }
  return fallback;
}

function readPositiveNumber(
  params: URLSearchParams,
  name: string,
  fallback: number | null,
  inputLogger: Logger
): number | null {
  if (!params.has(name)) return fallback;
  const raw = params.get(name);
  const value = raw === null ? Number.NaN : Number(raw);
  if (!Number.isFinite(value) || value <= 0) {
    inputLogger.warn('readPositiveNumber:invalid-value', { name, raw });
    return fallback;
  }
  return value;
}

function readPositiveInteger(
  params: URLSearchParams,
  name: string,
  fallback: number | null,
  inputLogger: Logger
): number | null {
  if (!params.has(name)) return fallback;
  const raw = params.get(name);
  const value = raw === null ? Number.NaN : Number(raw);
  if (!Number.isInteger(value) || value <= 0) {
    inputLogger.warn('readPositiveInteger:invalid-value', { name, raw });
    return fallback;
  }
  return value;
}
